import { useCallback, useEffect, useState } from 'react'
import { Alert, BackHandler, Platform, Pressable, StyleSheet, ScrollView, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DS } from '../../design-system'
import { ScheduledGameCard } from '../../components/ScheduledGameCard/ScheduledGameCard'
import Clipboard from '@react-native-clipboard/clipboard'
import ShareGameQR from '../../components/ShareGameQR/ShareGameQR'
import { useStore } from '../../models'
import { translate } from '../../i18n'
import { events } from '../../models/analytics/analytics-events'
import { formatBlindIncrease, formatGameScheduledAt, readScheduleGameConfig } from '../../utils'
import { getShareBaseUrl } from '../../utils/shareUrl'
import { getRouteToLastVisitedTab } from '../../navigation'
import { WebWrapper } from '@/components/WebWrapper'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

export const ScheduledTournamentConfirmationScreen = observer(() => {
  const router = useRouter()
  const { hash } = useLocalSearchParams<{ hash: string }>()

  const {
    share: { promptShare },
    session: { lastVisitedTab, user },
    game: { setToast },
    analytics
  } = useStore()

  const userId = user?.auth0Id ?? undefined
  const schedule = useQuery(api.gameSchedules.queries.getScheduleByHash, hash ? { hash, userId } : 'skip')
  const narrowedSchedule = schedule && !('restricted' in schedule) ? schedule : null
  const joinScheduledGame = useMutation(api.gameSchedules.mutations.joinPrivateScheduledGame)
  const cancelScheduleMutation = useMutation(api.gameSchedules.mutations.cancelSchedule)
  const [isStarting, setIsStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  const onStartGame = useCallback(async () => {
    if (!narrowedSchedule || !user?.auth0Id) return
    setStartError(null)
    setIsStarting(true)
    try {
      const result = await joinScheduledGame({
        scheduleId: narrowedSchedule._id,
        userId: user.auth0Id,
        userName: user.name ?? translate('scheduledTournamentConfirmation.defaultHostName')
      })
      router.replace({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as Parameters<
        typeof router.replace
      >[0])
    } catch (error) {
      console.error('Failed to start game:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start game'
      let userMessage = errorMessage
      if (errorMessage.includes('at least') && errorMessage.includes('players')) {
        userMessage = translate('scheduledTournamentConfirmation.startNeedMorePlayers')
      }
      setStartError(userMessage)
      if (Platform.OS !== 'web') {
        setToast({ kind: 'Error', message: userMessage })
      }
      setIsStarting(false)
    }
  }, [narrowedSchedule, user, joinScheduledGame, router, setToast])

  const scheduleConfig = readScheduleGameConfig(narrowedSchedule)
  const formattedSchedule = narrowedSchedule
    ? {
        name: narrowedSchedule.name,
        gameType: 'Tournament',
        blindTime: formatBlindIncrease(scheduleConfig.blindTimer),
        startingChips: String(scheduleConfig.startingChips ?? 0),
        turnDuration: `${scheduleConfig.turnDuration ?? 30} sec`,
        scheduledAt: new Date(narrowedSchedule.scheduledStartTime),
        hash: narrowedSchedule.hash ?? '',
        hostedBy: narrowedSchedule.hostName,
        isHost: narrowedSchedule.hostId === user?.auth0Id
      }
    : null

  const onClose = useCallback(() => {
    router.replace(getRouteToLastVisitedTab(lastVisitedTab))
  }, [router, lastVisitedTab])

  const openQrLightBox = () => {
    router.navigate('/lobby/qr-code')
  }

  const onShareGame = useCallback(() => {
    if (!formattedSchedule) return
    const date = formatGameScheduledAt(formattedSchedule.scheduledAt)
    const message = translate('scheduledTournamentConfirmation.shareMessage', { date })
    const joinPath = `/game/join/accept-scheduled-invite/${formattedSchedule.hash}`
    const qrUrl = `${getShareBaseUrl()}${joinPath}`
    promptShare(message, formattedSchedule.name, qrUrl)
    analytics.setEvent(events.game.scheduled_game_shared, {
      game_name: formattedSchedule.name
    })
  }, [formattedSchedule, promptShare, analytics])

  const goToEditScheduleScreen = () => {
    router.navigate(`/game/create/scheduled-tournament?hash=${hash}&isEdit=true`)
  }

  const onCopyGameCode = () => {
    if (formattedSchedule?.hash) {
      Clipboard.setString(formattedSchedule.hash.toUpperCase())
      setToast({
        kind: 'Game Code',
        message: translate('scheduledTournamentConfirmation.toastCodeCopied')
      })
    }
  }

  const handleConfirmDelete = useCallback(async () => {
    if (!narrowedSchedule) return
    setIsDeleteModalVisible(false)
    setIsDeleting(true)
    try {
      await cancelScheduleMutation({ scheduleId: narrowedSchedule._id })
      setToast({ kind: 'Game Code', message: translate('scheduledTournamentConfirmation.toastGameDeleted') })
      onClose()
    } catch (error) {
      console.error('Failed to delete schedule:', error)
      setToast({
        kind: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete game'
      })
      setIsDeleting(false)
    }
  }, [narrowedSchedule, cancelScheduleMutation, setToast, onClose])

  const onDeleteGame = useCallback(() => {
    if (!narrowedSchedule || !formattedSchedule?.isHost) return
    if (Platform.OS === 'ios') {
      Alert.alert(
        translate('scheduledTournamentConfirmation.deleteModal.title'),
        translate('scheduledTournamentConfirmation.deleteModal.message'),
        [
          { text: translate('common.cancel'), style: 'cancel' },
          {
            text: translate('scheduledTournamentConfirmation.deleteModal.confirm'),
            style: 'destructive',
            onPress: handleConfirmDelete
          }
        ]
      )
    } else {
      setIsDeleteModalVisible(true)
    }
  }, [narrowedSchedule, formattedSchedule?.isHost, handleConfirmDelete])

  useEffect(() => {
    // Close if hash is missing or schedule was not found (null means not found, undefined means loading)
    if (!hash || schedule === null) {
      onClose()
    }
  }, [hash, schedule, onClose])

  useEffect(() => {
    const listener = BackHandler.addEventListener('hardwareBackPress', () => {
      return true
    })
    return () => {
      listener.remove()
    }
  }, [])

  if (!formattedSchedule) {
    return (
      <SafeAreaView style={styles.container}>
        <WebWrapper>
          <DS.Frame style={styles.loadingContainer}>
            <DS.Text variant="Body Medium/Regular" color="Global/White">
              {translate('scheduledTournamentConfirmation.loading')}
            </DS.Text>
          </DS.Frame>
        </WebWrapper>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <WebWrapper>
        <DS.Frame row justifyContent="space-between" alignItems="center" style={styles.headerContainer}>
          <Pressable onPress={onClose}>
            <DS.Icon name="Close" size={24} onPress={onClose} color={'Global/White'} />
          </Pressable>
          {formattedSchedule.isHost && (
            <DS.Text variant="Body Medium/Medium" color={'Global/White'} onPress={goToEditScheduleScreen}>
              {translate('scheduledTournamentConfirmation.editGame')}
            </DS.Text>
          )}
        </DS.Frame>

        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
          <ScheduledGameCard {...formattedSchedule} onCopyGameCode={onCopyGameCode} onShareGame={onShareGame} />

          {formattedSchedule.isHost && narrowedSchedule && (narrowedSchedule.usersToNotify?.length ?? 0) > 0 && (
            <DS.Frame style={styles.acceptedSection} gap="8">
              <DS.Text variant="Heading 4/Medium" color="Global/White">
                {translate('scheduledTournamentConfirmation.acceptedInvites.title')}
              </DS.Text>
              {(narrowedSchedule.acceptedInvites?.length ?? 0) > 0 ? (
                <>
                  <DS.Text variant="Body Medium/Regular" color="Global/White">
                    {(narrowedSchedule.acceptedInvites?.length ?? 0) === 1
                      ? translate('scheduledTournamentConfirmation.acceptedInvites.countSingular', { count: 1 })
                      : translate('scheduledTournamentConfirmation.acceptedInvites.countPlural', {
                          count: narrowedSchedule.acceptedInvites?.length ?? 0
                        })}
                  </DS.Text>
                  <DS.Frame gap="4" style={styles.namesList}>
                    {(narrowedSchedule.acceptedInvites ?? []).map((inv) => (
                      <DS.Text key={inv.userId} variant="Body Small/Medium" color="Global/White">
                        • {inv.userName}
                      </DS.Text>
                    ))}
                  </DS.Frame>
                </>
              ) : (
                <DS.Text variant="Body Medium/Regular" color="Global/White">
                  {(narrowedSchedule.usersToNotify ?? []).length === 1
                    ? translate('scheduledTournamentConfirmation.acceptedInvites.countSingular', { count: 1 })
                    : translate('scheduledTournamentConfirmation.acceptedInvites.countPlural', {
                        count: (narrowedSchedule.usersToNotify ?? []).length
                      })}
                </DS.Text>
              )}
            </DS.Frame>
          )}

          {formattedSchedule.isHost && (
            <DS.Frame style={styles.startGameSection} gap="8">
              {Platform.OS === 'web' && startError && (
                <DS.Text variant="Body Small/Regular" color="Utility/Warning" style={styles.startError}>
                  {startError}
                </DS.Text>
              )}
              <DS.Button variant="Large/Primary" type="On Dark" onPress={onStartGame} disabled={isStarting}>
                {isStarting
                  ? translate('scheduledTournamentConfirmation.starting')
                  : translate('scheduledTournamentConfirmation.startGame')}
              </DS.Button>
              <DS.Button
                variant="Large/Secondary"
                type="On Light"
                onPress={onDeleteGame}
                disabled={isDeleting || isStarting}
              >
                {isDeleting
                  ? translate('scheduledTournamentConfirmation.deleting')
                  : translate('scheduledTournamentConfirmation.deleteGame')}
              </DS.Button>
            </DS.Frame>
          )}
          <DS.Frame style={styles.shareGameQr}>
            <ShareGameQR onPress={openQrLightBox} />
          </DS.Frame>
        </ScrollView>
      </WebWrapper>

      {isDeleteModalVisible && (
        <View style={styles.deleteOverlay}>
          <Pressable style={styles.deleteBackdrop} onPress={() => setIsDeleteModalVisible(false)} />
          <View style={styles.deleteModalContainer}>
            <View style={styles.deleteModalContent}>
              <DS.Text variant="Heading 2/Medium" textAlign="center">
                {translate('scheduledTournamentConfirmation.deleteModal.title')}
              </DS.Text>
              <DS.Text variant="Body Small/Regular" textAlign="center" color="Brand/Dark Grey">
                {translate('scheduledTournamentConfirmation.deleteModal.message')}
              </DS.Text>
              <DS.Frame gap="8" alignItems="center">
                <DS.Button
                  variant="Medium/Primary"
                  type="On Light"
                  onPress={() => setIsDeleteModalVisible(false)}
                  style={styles.modalButton}
                >
                  {translate('common.cancel')}
                </DS.Button>
                <DS.Button
                  variant="Medium/Secondary"
                  type="On Dark"
                  onPress={handleConfirmDelete}
                  style={styles.modalButton}
                >
                  {translate('scheduledTournamentConfirmation.deleteModal.confirm')}
                </DS.Button>
              </DS.Frame>
            </View>
          </View>
        </View>
      )}
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Purple'],
    padding: DS.spacing['24'],
    paddingBottom: 0
  },
  headerContainer: {
    width: '100%',
    marginBottom: DS.spacing['12']
  },
  titleContainer: {
    marginTop: DS.spacing['32'],
    marginBottom: DS.spacing['24']
  },
  acceptedSection: {
    marginTop: DS.spacing['24'],
    paddingVertical: DS.spacing['16'],
    paddingHorizontal: DS.spacing['16'],
    backgroundColor: `${DS.palette['Global/White']}14`,
    borderRadius: 12
  },
  namesList: {
    marginTop: DS.spacing['4']
  },
  startGameSection: {
    marginTop: DS.spacing['24']
  },
  startError: {
    textAlign: 'center'
  },
  scrollContent: {
    flexGrow: 1
  },
  shareGameQr: {
    flex: 1,
    marginTop: DS.spacing['24'],
    justifyContent: 'flex-end'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  deleteOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center'
  },
  deleteBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  deleteModalContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    alignItems: 'center',
    marginHorizontal: 24,
    paddingHorizontal: 24,
    borderRadius: 20,
    maxWidth: 400,
    width: '85%'
  },
  deleteModalContent: {
    width: '100%',
    paddingVertical: 24,
    gap: 20
  },
  modalButton: {
    width: '100%'
  }
})
